<template>
	<div class="local-video">
		<div class="avatar" :style="{ 'height': height, 'width': width }" v-show="!isShowVideo">
			<head-image class="head-image" :url="safeUserInfo.headImage" :name="displayName" :size="64">
			</head-image>
		</div>
		<video v-show="isShowVideo" class="video" :class="safeUserInfo.isShareScreen ? 'contain' : ''" ref="video"></video>
	</div>
</template>

<script>
import HeadImage from '@/components/common/HeadImage'
import { hasUsableVideoFrame } from '@/utils/rtcVideoPreview'
export default {
	name: "localVideo",
	components: {
		HeadImage
	},
	data() {
		return {
			stream: null,
			isReady: false,
			isFacing: true,
			previewRetryTimer: null,
				previewRetryCount: 0,
				previewTracks: []
		}
	},
	props: {
		userInfo: {
			type: Object
		},
		width: {
			type: Number
		},
		height: {
			type: Number
		},
	},
	methods: {
		open(stream) {
			this.clearPreviewMonitor();
			const video = this.$refs.video;
			const alreadyReady = video && video.srcObject === stream && hasUsableVideoFrame(video, stream);
			this.stream = stream || null;
			this.isReady = Boolean(alreadyReady);
			const hasVideoTrack = Boolean(stream && stream.getVideoTracks && stream.getVideoTracks().length);
			if (!video || !hasVideoTrack) {
				if (video) video.srcObject = null;
				return;
			}
			video.srcObject = stream;
			video.muted = true;
			video.autoplay = true;
			video.playsInline = true;
			this.previewRetryCount = 0;
			video.onloadeddata = this.updatePreviewReadiness;
			video.oncanplay = this.updatePreviewReadiness;
			video.onplaying = this.updatePreviewReadiness;
			video.onresize = this.updatePreviewReadiness;
			video.ontimeupdate = this.updatePreviewReadiness;
			this.previewTracks = stream.getVideoTracks();
			this.previewTracks.forEach(track => {
				if (!track.addEventListener) return;
				track.addEventListener('ended', this.updatePreviewReadiness);
				track.addEventListener('mute', this.updatePreviewReadiness);
				track.addEventListener('unmute', this.updatePreviewReadiness);
			});
			if (!this.isReady) {
				Promise.resolve(video.play()).then(() => {
					this.updatePreviewReadiness();
					if (!this.isReady) this.schedulePreviewCheck(false);
				}).catch(() => this.schedulePreviewCheck(true));
				this.schedulePreviewCheck(false);
			}
		},
		updatePreviewReadiness() {
			const video = this.$refs.video;
			this.isReady = hasUsableVideoFrame(video, this.stream);
			if (this.isReady && this.previewRetryTimer) {
				clearTimeout(this.previewRetryTimer);
				this.previewRetryTimer = null;
			}
		},
		schedulePreviewCheck(retryPlayback) {
			if (this.previewRetryTimer || this.previewRetryCount >= 40) return;
			this.previewRetryCount += 1;
			this.previewRetryTimer = setTimeout(() => {
				this.previewRetryTimer = null;
				const video = this.$refs.video;
				if (!video || !this.stream) return;
				this.updatePreviewReadiness();
				if (this.isReady || this.previewRetryCount >= 40) return;
				if (retryPlayback || video.paused) Promise.resolve(video.play()).catch(() => {});
				this.schedulePreviewCheck(true);
			}, 100);
		},
		clearPreviewMonitor() {
			if (this.previewRetryTimer) clearTimeout(this.previewRetryTimer);
			this.previewRetryTimer = null;
			this.previewTracks.forEach(track => {
				if (!track.removeEventListener) return;
				track.removeEventListener('ended', this.updatePreviewReadiness);
				track.removeEventListener('mute', this.updatePreviewReadiness);
				track.removeEventListener('unmute', this.updatePreviewReadiness);
			});
			this.previewTracks = [];
			const video = this.$refs.video;
			if (!video) return;
			video.onloadeddata = null;
			video.oncanplay = null;
			video.onplaying = null;
			video.onresize = null;
			video.ontimeupdate = null;
		},
	},
	computed: {
		safeUserInfo() {
			return this.userInfo || {};
		},
		displayName() {
			return this.safeUserInfo.showNickName || this.safeUserInfo.nickName || this.safeUserInfo.userName || '我';
		},
		isShowVideo() {
			return (this.safeUserInfo.isCamera === true || this.safeUserInfo.isShareScreen === true)
				&& this.isReady && hasUsableVideoFrame(this.$refs.video, this.stream);
		}
	},
	beforeDestroy() {
		this.clearPreviewMonitor();
	}
}
</script>

<style lang="scss" scoped>
.local-video {
	position: relative;
	width: 100%;
	height: 100%;
	background: var(--im-chat-bg, var(--im-background));
	cursor: pointer;
	overflow: hidden;
	border: 2px solid var(--im-border-light);
	box-sizing: border-box;

	.avatar {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 100%;
		overflow: hidden;
	}

	.video {
		position: relative;
		width: 100%;
		height: 100%;
		object-fit: cover;
		background: var(--rtc-group-surface, var(--im-chat-bg, var(--im-background)));

		&.contain {
			object-fit: contain;
		}
	}
}
</style>
